import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import api from '../services/api';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');

  const token = new URLSearchParams(window.location.search).get('token') || '';

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Missing verification token in link.');
      return;
    }
    (async () => {
      try {
        const res = await api.request('/auth/verify-email', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });
        setEmail(res.email || '');
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError(err.message || 'Verification failed');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950" data-testid="verify-email-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-soft-lg p-8 border border-slate-200 dark:border-slate-800 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-brand-deep-blue animate-spin mb-4" />
              <h2 className="text-xl font-heading font-bold mb-2">Verifying your email…</h2>
              <p className="text-sm text-muted-foreground">Please wait while we confirm your address.</p>
            </>
          )}
          {status === 'success' && (
            <div data-testid="verify-success">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500 flex items-center justify-center mb-3">
                <Check className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-heading font-bold mb-2">Email verified!</h2>
              <p className="text-sm text-muted-foreground mb-5">
                {email ? <strong>{email}</strong> : 'Your email'} is now verified. You can sign in.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">Go to Sign In</Button>
            </div>
          )}
          {status === 'error' && (
            <div data-testid="verify-error">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-3">
                <Mail className="w-7 h-7 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-heading font-bold mb-2">Verification failed</h2>
              <Alert variant="destructive" className="mb-4 text-left">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button onClick={() => navigate('/login')} className="w-full">Back to Sign In</Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
